import { supabase } from '../lib/supabase'
import { obtenerConteosCursosPorSocio } from './socioCursosService'

const socioColumns = 'id, organizacion_id, usuario_id, numero_socio, rut, nombres, apellido_paterno, apellido_materno, email, telefono, direccion, comuna, fecha_ingreso, estado, observaciones, created_at, updated_at'
const editableFields = ['numero_socio', 'rut', 'nombres', 'apellido_paterno', 'apellido_materno', 'email', 'telefono', 'direccion', 'comuna', 'fecha_ingreso', 'estado', 'observaciones']

function requireOrganization(organizacionId) {
  if (!organizacionId) throw new Error('No fue posible identificar la organización.')
}

function editablePayload(values) {
  return editableFields.reduce((payload, field) => {
    payload[field] = values[field] === '' ? null : values[field]
    return payload
  }, {})
}

function serviceError(error) {
  if (error?.code === '23505') return new Error('Ya existe un socio con este RUT.')
  return new Error('No fue posible completar la operación. Intenta nuevamente.')
}

function searchWords(search) {
  return search
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}@._+-]/gu, ''))
    .filter(Boolean)
}

function rutSearchTerms(word) {
  const terms = [word]
  const compact = word.replace(/[^0-9kK]/g, '').toUpperCase()

  if (/^[0-9.\-kK]+$/.test(word) && compact.length >= 7) {
    const hasCheckDigit = word.includes('-')
    const body = hasCheckDigit ? compact.slice(0, -1) : compact
    const checkDigit = hasCheckDigit ? compact.slice(-1) : ''
    const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    terms.push(compact, `${formattedBody}${checkDigit ? `-${checkDigit}` : ''}`)
  }

  return [...new Set(terms)]
}

export async function obtenerSocios({ organizacionId, page = 1, pageSize = 20, estado = 'todos', search = '' }) {
  requireOrganization(organizacionId)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1
  const words = searchWords(search)

  let query = supabase
    .from('cp_socios')
    .select(socioColumns, { count: 'exact' })
    .eq('organizacion_id', organizacionId)

  if (estado !== 'todos') query = query.eq('estado', estado)
  for (const word of words) {
    const filters = [
      ...rutSearchTerms(word).map((rutTerm) => `rut.ilike.%${rutTerm}%`),
      `nombres.ilike.%${word}%`,
      `apellido_paterno.ilike.%${word}%`,
      `apellido_materno.ilike.%${word}%`,
      `email.ilike.%${word}%`,
    ]
    query = query.or(filters.join(','))
  }

  const { data, count, error } = await query.order('created_at', { ascending: false }).range(from, to)
  if (error) throw serviceError(error)
  const socios = data ?? []
  const courseCounts = await obtenerConteosCursosPorSocio(organizacionId, socios.map((socio) => socio.id))
  return {
    socios: socios.map((socio) => ({ ...socio, cursos_count: courseCounts[socio.id] ?? 0 })),
    total: count ?? 0,
  }
}

export async function obtenerSocioPorId(organizacionId, socioId) {
  requireOrganization(organizacionId)
  const { data, error } = await supabase
    .from('cp_socios')
    .select(socioColumns)
    .eq('organizacion_id', organizacionId)
    .eq('id', socioId)
    .maybeSingle()

  if (error) throw serviceError(error)
  if (!data) throw new Error('Socio no encontrado.')
  return data
}

export async function crearSocio(organizacionId, values) {
  requireOrganization(organizacionId)
  const { data, error } = await supabase
    .from('cp_socios')
    .insert({ ...editablePayload(values), organizacion_id: organizacionId })
    .select(socioColumns)
    .single()

  if (error) throw serviceError(error)
  return data
}

export async function actualizarSocio(organizacionId, socioId, values) {
  requireOrganization(organizacionId)
  const { data, error } = await supabase
    .from('cp_socios')
    .update(editablePayload(values))
    .eq('organizacion_id', organizacionId)
    .eq('id', socioId)
    .select(socioColumns)
    .maybeSingle()

  if (error) throw serviceError(error)
  if (!data) throw new Error('Socio no encontrado.')
  return data
}

export async function cambiarEstadoSocio(organizacionId, socioId, estado) {
  requireOrganization(organizacionId)
  if (!['activo', 'inactivo'].includes(estado)) throw new Error('Estado no permitido.')

  const { data, error } = await supabase
    .from('cp_socios')
    .update({ estado })
    .eq('organizacion_id', organizacionId)
    .eq('id', socioId)
    .select(socioColumns)
    .maybeSingle()

  if (error) throw serviceError(error)
  if (!data) throw new Error('Socio no encontrado.')
  return data
}
